/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from 'react';

import { StyledFilterDrawer } from '@components/views/components/listing/drawer/ResponsiveListingFilterAndSortStyle';
import ResponsiveFilterSortButton from '@components/views/components/listing/drawer/FilterSortButton/FilterSortButton';
import ResponsiveListingSort from '@components/views/components/listing/drawer/ResponsiveListingSort';
import { FlexibleDiv } from '@components/formStyles';
import { CAR_SORT_DATA, FILTERS_QUERY_PARAMS } from '@constants/constants';
import ClearAllFilters from '@components/views/components/ClearAllFilters/ClearAllFilters';
import PriceRangeSlider from '@shared/PriceRangeSlider';
import CollapsibleMultiSelectionFilter from '@components/ui/filters/MultiSelectionFilter/CollapsibleMultiSelectionFilter';
import { isEmptyFilters } from '@components/views/utils';
import ResponsiveApplyButton from '@components/views/components/listing/drawer/ResponsiveApplyButton/ResponsiveApplyButton';
import { CAR_RENTAL_FILTER_QUERY_PARAM_KEYS } from '@page/carRental/list/constants';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import palette from '@utils/theme/palette';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';
import { getFilterByKey, getPriceRange } from '@domain/carRental/utils';
import {
  getActiveSortFromQuery,
  getSelectedFilterByKeyNameFromQuery,
} from '@utils/carRentalFilterUtils';
import STATIC_ROUTES from '@data/staticRoutes.json';
import { useAppRouter } from '@contexts/router';
import clean from '@utils/cleanObject';
import { CarRentalListFilterProps } from '@domain/carRental/hooks';

import {
  MobileFilterButtonWrapper,
  SectionWrapperStyled,
} from '../Filters.styled';

export default function CarRentalListFiltersMobile({
  filters,
  prices,
  setPrices,
  fuelTypes,
  setFuelTypes,
  transmissionTypes,
  setTransmissionTypes,
  vehicleClasses,
  setVehicleClasses,
  providers,
  setProviders,
  doorCounts,
  setDoorCounts,
  seatCounts,
  setSeatCounts,
  luggageCounts,
  setLuggageCounts,
}: CarRentalListFilterProps) {
  const query = useSearchParamsToQuery();
  const { push } = useAppRouter();
  const [isShowFilters, setIsShowFilters] = useState(false);

  const [activeOrderKey, setActiveOrderKey] = useState(
    getActiveSortFromQuery(query)?.id || CAR_SORT_DATA[0].id
  );
  const { priceRanges } = getPriceRange(filters);

  useSkipEffectOnMount(() => {
    if (isShowFilters) {
      setFuelTypes(
        getSelectedFilterByKeyNameFromQuery(
          query,
          CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.FUEL_TYPES
        )
      );
      setTransmissionTypes(
        getSelectedFilterByKeyNameFromQuery(
          query,
          CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.TRANSMISSION_TYPES
        )
      );
      setVehicleClasses(
        getSelectedFilterByKeyNameFromQuery(
          query,
          CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.VEHICLE_CLASSES
        )
      );

      setProviders(
        getSelectedFilterByKeyNameFromQuery(
          query,
          CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PROVIDERS
        )
      );

      setDoorCounts(
        getSelectedFilterByKeyNameFromQuery(
          query,
          CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.DOOR_COUNTS
        )
      );
      setSeatCounts(
        getSelectedFilterByKeyNameFromQuery(
          query,
          CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.SEAT_COUNTS
        )
      );
      setLuggageCounts(
        getSelectedFilterByKeyNameFromQuery(
          query,
          CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.LUGGAGE_COUNTS
        )
      );

      const priceMin =
        parseInt(
          query?.[CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PRICE_MIN] as string
        ) || prices[0];
      const priceMax =
        parseInt(
          query?.[CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PRICE_MAX] as string
        ) || prices[1];

      setPrices(priceMin && priceMax ? [priceMin, priceMax] : []);

      setActiveOrderKey(
        getActiveSortFromQuery(query)?.id || CAR_SORT_DATA[0].id
      );
    }
  }, [isShowFilters, query]);

  /**
   * Check all filters are empty
   * If all filters are empty, clear all filters button should be disabled
   * If any filter is not empty, clear all filters button should be enabled
   * @returns boolean
   */
  const checkEmptyFilters = () => {
    let isEmptyFilter = true;

    const isEmptyFiltersResult = isEmptyFilters(query);

    if (!isEmptyFiltersResult) {
      isEmptyFilter = false;
    }

    return isEmptyFilter;
  };

  const handleApplyFilters = (): void => {
    const { page, ...nextQuery } = query;

    push({
      pathname: STATIC_ROUTES.carRentalSearchPathPrefix ?? '',
      query: clean({
        ...nextQuery,
        [CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.FUEL_TYPES]: fuelTypes,
        [CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.TRANSMISSION_TYPES]:
          transmissionTypes,
        [CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.VEHICLE_CLASSES]: vehicleClasses,
        [CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.DOOR_COUNTS]: doorCounts,
        [CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.SEAT_COUNTS]: seatCounts,
        [CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.LUGGAGE_COUNTS]: luggageCounts,
        [CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.SORT]: activeOrderKey,
        [CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PRICE_MIN]: prices[0],
        [CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PRICE_MAX]: prices[1],
        [CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PROVIDERS]: providers,
      }),
    });

    setIsShowFilters(false);
  };

  const handleClearAllFilters = () => {
    setFuelTypes([]);
    setTransmissionTypes([]);
    setVehicleClasses([]);
    setProviders([]);
    setDoorCounts([]);
    setSeatCounts([]);
    setLuggageCounts([]);
    setPrices(
      priceRanges?.[0] && priceRanges?.[1]
        ? [priceRanges[0], priceRanges?.[1]]
        : []
    );

    const { page, ...nextQuery } = query;

    /**
     * Remove all filters from query
     */
    for (let i = 0; i < FILTERS_QUERY_PARAMS.length; i++) {
      delete nextQuery[FILTERS_QUERY_PARAMS[i]];
    }

    push({
      pathname: STATIC_ROUTES.carRentalSearchPathPrefix ?? '',
      query: nextQuery,
    });

    setIsShowFilters(false);
  };

  return (
    <>
      <MobileFilterButtonWrapper>
        <ResponsiveFilterSortButton
          title='Filtrele ve Sırala'
          customBackgroundColor={palette.white}
          onClick={() => setIsShowFilters(true)}
        />
      </MobileFilterButtonWrapper>
      <StyledFilterDrawer
        title='Filtrele ve Sırala'
        width='100%'
        height='100%'
        placement='top'
        visible={isShowFilters}
        onClose={() => setIsShowFilters(false)}
        footer={
          <ResponsiveApplyButton title='Uygula' onClick={handleApplyFilters} />
        }
      >
        <FlexibleDiv bottom={20} top={20}>
          <ResponsiveListingSort
            data={CAR_SORT_DATA}
            activeKey={activeOrderKey}
            handleTabClick={(key) => setActiveOrderKey(key)}
          />
        </FlexibleDiv>
        <FlexibleDiv bottom={20}>
          <ClearAllFilters
            onClick={handleClearAllFilters}
            disabled={checkEmptyFilters()}
          />
        </FlexibleDiv>
        {priceRanges?.[0] && priceRanges?.[1] ? (
          <SectionWrapperStyled className='carRentalListFiltersMobile__priceRange'>
            <PriceRangeSlider
              onChange={(values) => setPrices(values)}
              values={prices as [number, number]}
              min={priceRanges[0]}
              max={priceRanges[1]}
            />
          </SectionWrapperStyled>
        ) : null}
        {getFilterByKey(filters, CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.FUEL_TYPES)
          ?.length ? (
          <SectionWrapperStyled>
            <CollapsibleMultiSelectionFilter
              title='Yakıt Tipi'
              selectedItems={fuelTypes}
              data={getFilterByKey(
                filters,
                CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.FUEL_TYPES
              )}
              handleSelectedItems={(items) => setFuelTypes(items)}
              subTitleKey='count'
            />
          </SectionWrapperStyled>
        ) : null}

        {getFilterByKey(
          filters,
          CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.TRANSMISSION_TYPES
        )?.length ? (
          <SectionWrapperStyled>
            <CollapsibleMultiSelectionFilter
              title='Vites Tipi'
              selectedItems={transmissionTypes}
              data={getFilterByKey(
                filters,
                CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.TRANSMISSION_TYPES
              )}
              subTitleKey='count'
              handleSelectedItems={(items) => setTransmissionTypes(items)}
            />
          </SectionWrapperStyled>
        ) : null}

        {getFilterByKey(
          filters,
          CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.VEHICLE_CLASSES
        )?.length ? (
          <SectionWrapperStyled>
            <CollapsibleMultiSelectionFilter
              title='Araç Tipi'
              selectedItems={vehicleClasses}
              data={getFilterByKey(
                filters,
                CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.VEHICLE_CLASSES
              )}
              subTitleKey='count'
              handleSelectedItems={(items) => setVehicleClasses(items)}
            />
          </SectionWrapperStyled>
        ) : null}

        {getFilterByKey(filters, CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PROVIDERS)
          ?.length ? (
          <SectionWrapperStyled>
            <CollapsibleMultiSelectionFilter
              title='Sağlayıcı Firma'
              selectedItems={providers}
              data={getFilterByKey(
                filters,
                CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PROVIDERS
              )}
              subTitleKey='count'
              handleSelectedItems={(items) => {
                setProviders(items);
              }}
            />
          </SectionWrapperStyled>
        ) : null}

        {getFilterByKey(filters, CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.DOOR_COUNTS)
          ?.length ? (
          <SectionWrapperStyled>
            <CollapsibleMultiSelectionFilter
              title='Kapı Sayısı'
              selectedItems={doorCounts}
              data={getFilterByKey(
                filters,
                CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.DOOR_COUNTS
              )}
              subTitleKey='count'
              handleSelectedItems={(items) => setDoorCounts(items)}
            />
          </SectionWrapperStyled>
        ) : null}

        {getFilterByKey(filters, CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.SEAT_COUNTS)
          ?.length ? (
          <SectionWrapperStyled>
            <CollapsibleMultiSelectionFilter
              title='Koltuk Sayısı'
              selectedItems={seatCounts}
              data={getFilterByKey(
                filters,
                CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.SEAT_COUNTS
              )}
              subTitleKey='count'
              handleSelectedItems={(items) => setSeatCounts(items)}
            />
          </SectionWrapperStyled>
        ) : null}

        {getFilterByKey(
          filters,
          CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.LUGGAGE_COUNTS
        )?.length ? (
          <SectionWrapperStyled>
            <CollapsibleMultiSelectionFilter
              title='Bagaj Sayısı'
              selectedItems={luggageCounts}
              data={getFilterByKey(
                filters,
                CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.LUGGAGE_COUNTS
              )}
              subTitleKey='count'
              handleSelectedItems={(items) => setLuggageCounts(items)}
            />
          </SectionWrapperStyled>
        ) : null}
      </StyledFilterDrawer>
    </>
  );
}
